import { useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const emptyProduct = {
  title: "", description: "", price: "", type: "physical",
  category: "art", image_url: "", images: [], digital_file_path: "",
  preview_url: "", stock: "", delivery_days: 15,
  free_shipping: false, shipping_price: "", allow_pickup: false, shipping_vat_included: false,
  event_dates: ["", "", ""], event_location: "", event_contact: "",
  apparel_section: "men", one_size: false, sizes: [], colors: [], variant_stock: {},
  stream_type: "audiobook", chapters: [],
};

const readDur = (file) => new Promise((resolve, reject) => {
  const isVideo = file.type.startsWith("video/");
  const el = document.createElement(isVideo ? "video" : "audio");
  el.preload = "metadata";
  el.onloadedmetadata = () => { const d = el.duration; URL.revokeObjectURL(el.src); (!isFinite(d) || d <= 0) ? reject(new Error("bad")) : resolve(d); };
  el.onerror = () => reject(new Error("bad"));
  el.src = URL.createObjectURL(file);
});

const fmtDur = (s) => { if (!s || s < 0) return "0:00"; const h=Math.floor(s/3600), m=Math.floor((s%3600)/60), sec=Math.floor(s%60); return h>0 ? `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}` : `${m}:${String(sec).padStart(2,"0")}`; };

const emptyNewSeller = {
  name: "", email: "", shop_name: "", picture: "",
  create_login: false, password: "",
  payout: { method: "paypal", paypal_email: "", remit_phone: "", remit_country: "", bank_details: "" },
};

export default function AdminAddProduct({ sellers, onDone }) {
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [selectedSellerId, setSelectedSellerId] = useState("");
  const [newSeller, setNewSeller] = useState(emptyNewSeller);
  const [form, setForm] = useState(emptyProduct);
  const [submitting, setSubmitting] = useState(false);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const url = `${BACKEND_URL}${data.url}`;
    setForm((f) => ({ ...f, image_url: f.image_url || url, images: [...(f.images || []), url] }));
    toast.success("Image uploaded");
  };

  const removeImage = (url) => {
    setForm((f) => {
      const images = (f.images || []).filter((u) => u !== url);
      return { ...f, images, image_url: images[0] || "" };
    });
  };

  const uploadDigital = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/digital", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, digital_file_path: data.path }));
    toast.success(`File uploaded: ${data.original_name}`);
  };

  const uploadPreview = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, preview_url: `${BACKEND_URL}${data.url}` }));
    toast.success("Preview uploaded");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 1. Resolve seller_id: use existing or create new
      let sellerId = selectedSellerId;
      if (mode === "new") {
        if (!newSeller.name.trim()) {
          toast.error("Artisan name is required");
          setSubmitting(false); return;
        }
        if (newSeller.create_login && (!newSeller.email || newSeller.password.length < 6)) {
          toast.error("Email + password (min 6 chars) required for login accounts");
          setSubmitting(false); return;
        }
        const payoutPayload = { ...newSeller.payout };
        const hasPayout = payoutPayload.paypal_email || payoutPayload.remit_phone || payoutPayload.bank_details;
        const { data: created } = await api.post("/admin/sellers", {
          name: newSeller.name.trim(),
          email: newSeller.email.trim() || null,
          shop_name: newSeller.shop_name.trim() || null,
          picture: newSeller.picture || null,
          create_login: newSeller.create_login,
          password: newSeller.create_login ? newSeller.password : null,
          payout: hasPayout ? payoutPayload : null,
        });
        sellerId = created.user_id;
        toast.success(`Artisan "${created.name}" created`);
      } else if (!sellerId) {
        toast.error("Pick an existing artisan or switch to Create new");
        setSubmitting(false); return;
      }

      // 2. Build product payload (mirror seller submission logic)
      const isTicket = form.category === "ticket";
      const isApparel = form.category === "apparel";
      const isStream = form.category === "stream";

      // --- Stream branch: POST /streams with chapters ---
      if (isStream) {
        if (!form.chapters || form.chapters.length === 0) { toast.error("Upload at least one chapter"); setSubmitting(false); return; }
        if (form.chapters.some((c) => c.uploading)) { toast.error("Wait for chapter uploads to finish"); setSubmitting(false); return; }
        const totalSec = form.chapters.reduce((s, c) => s + (c.duration_seconds || 0), 0);
        if (totalSec > 7200 + 0.5) { toast.error(`Combined duration ${(totalSec/60).toFixed(1)} min exceeds 2 hours — split into a Part 2`); setSubmitting(false); return; }
        const priceNum = parseFloat(form.price);
        if (isNaN(priceNum) || priceNum < 0) { toast.error("Stream price cannot be negative"); setSubmitting(false); return; }
        await api.post("/streams", {
          seller_id: sellerId,
          title: form.title,
          description: form.description,
          price: priceNum,
          stream_type: form.stream_type,
          image_url: form.image_url || null,
          chapters: form.chapters.map((c) => ({
            title: c.title || "Untitled",
            storage_key: c.storage_key,
            duration_seconds: c.duration_seconds,
            mime: c.mime,
            size_bytes: c.size_bytes,
          })),
        });
        toast.success("Stream added and auto-approved");
        setForm(emptyProduct);
        setNewSeller(emptyNewSeller);
        setMode("existing");
        setSelectedSellerId("");
        if (onDone) onDone();
        return;
      }

      const cleanDates = (form.event_dates || []).filter((d) => d && d.trim());
      if (isTicket && cleanDates.length === 0) {
        toast.error("Add at least one event date"); setSubmitting(false); return;
      }
      let sizes = null, variantStock = null;
      if (isApparel) {
        if (form.one_size) sizes = ["ONE SIZE"];
        else {
          sizes = (form.sizes || []).map((s) => s.trim()).filter(Boolean);
          if (sizes.length === 0) { toast.error("Add at least one size"); setSubmitting(false); return; }
        }
        variantStock = {};
        const colorList = (form.colors || []).filter((c) => c.name);
        const colorKeys = colorList.length ? colorList.map((c) => c.name) : [""];
        for (const s of sizes) for (const ck of colorKeys) {
          const k = `${s}|${ck}`;
          const v = parseInt(form.variant_stock?.[k] || 0);
          variantStock[k] = isNaN(v) ? 0 : Math.max(0, v);
        }
      }
      const payload = {
        seller_id: sellerId,
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        type: form.type,
        category: form.category,
        image_url: form.image_url || null,
        images: form.images || [],
        digital_file_path: form.digital_file_path || null,
        preview_url: form.preview_url || null,
        stock: form.stock ? parseInt(form.stock) : null,
        delivery_days: form.type === "physical" ? parseInt(form.delivery_days) : null,
        free_shipping: form.type === "physical" ? !!form.free_shipping : false,
        shipping_price: form.type === "physical" && !form.free_shipping && form.shipping_price !== "" ? parseFloat(form.shipping_price) : null,
        allow_pickup: form.type === "physical" && !form.free_shipping ? !!form.allow_pickup : false,
        shipping_vat_included: form.type === "physical" && !form.free_shipping ? !!form.shipping_vat_included : false,
        event_dates: isTicket ? cleanDates : null,
        event_location: isTicket ? form.event_location : null,
        event_contact: isTicket ? form.event_contact : null,
        apparel_section: isApparel ? form.apparel_section : null,
        sizes: isApparel ? sizes : null,
        colors: isApparel ? (form.colors || []).filter((c) => c.name && c.image_url) : null,
        variant_stock: isApparel ? variantStock : null,
      };
      if (form.type === "physical" && !form.free_shipping) {
        if (form.shipping_price === "" || parseFloat(form.shipping_price) < 0) {
          toast.error("Enter a shipping price or tick free shipping");
          setSubmitting(false); return;
        }
      }
      await api.post("/products", payload);
      toast.success("Product added and auto-approved");
      setForm(emptyProduct);
      setNewSeller(emptyNewSeller);
      setMode("existing");
      setSelectedSellerId("");
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="brut-border bg-white p-6 max-w-4xl space-y-6">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Back office</div>
        <h3 className="heading text-2xl font-bold uppercase mt-1">Add a product on behalf of an artisan</h3>
        <p className="text-sm text-[#555] mt-1">Products created here are auto-approved. You can assign to an existing seller or create a new artisan profile inline.</p>
      </div>

      {/* Artisan picker */}
      <div className="brut-border bg-[#FDF3E9] p-5 space-y-4">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Artisan</div>
        <div className="grid grid-cols-2 brut-border w-fit">
          <button type="button" data-testid="mode-existing" onClick={() => setMode("existing")}
            className={`px-5 py-3 mono uppercase text-xs tracking-[0.25em] ${mode === "existing" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
            Pick existing
          </button>
          <button type="button" data-testid="mode-new" onClick={() => setMode("new")}
            className={`px-5 py-3 mono uppercase text-xs tracking-[0.25em] border-l-2 border-[#1F3D2A] ${mode === "new" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
            Create new
          </button>
        </div>

        {mode === "existing" ? (
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Artisan</label>
            <select data-testid="pick-seller" required={mode === "existing"} value={selectedSellerId}
              onChange={(e) => setSelectedSellerId(e.target.value)}
              className="w-full mt-2 brut-border bg-white px-4 py-3">
              <option value="">— Select an artisan —</option>
              {sellers.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {s.shop_name || s.name} · {s.email}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Artisan name *</label>
                <input data-testid="new-seller-name" required value={newSeller.name}
                  onChange={(e) => setNewSeller({ ...newSeller, name: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Anya Maker" />
              </div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Shop name</label>
                <input data-testid="new-seller-shop" value={newSeller.shop_name}
                  onChange={(e) => setNewSeller({ ...newSeller, shop_name: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="Clay & Vinyl Studio" />
              </div>
              <div className="md:col-span-2">
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">
                  Email {newSeller.create_login ? "*" : "(optional)"}
                </label>
                <input data-testid="new-seller-email" type="email" value={newSeller.email}
                  onChange={(e) => setNewSeller({ ...newSeller, email: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3"
                  placeholder="artisan@studio.com — leave blank for admin-managed only" />
              </div>
            </div>

            <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
              <input data-testid="new-seller-login" type="checkbox" checked={newSeller.create_login}
                onChange={(e) => setNewSeller({ ...newSeller, create_login: e.target.checked })} />
              <span className="mono text-xs uppercase tracking-[0.2em]">Create login account (emails credentials)</span>
            </label>

            {newSeller.create_login && (
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Temporary password *</label>
                <input data-testid="new-seller-password" type="text" value={newSeller.password}
                  onChange={(e) => setNewSeller({ ...newSeller, password: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3"
                  placeholder="min 6 chars — emailed to seller who should change on first login" />
              </div>
            )}

            <div className="pt-3 border-t-2 border-dashed border-[#EAEAEA] space-y-3">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Payout details (how they will be paid)</div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Method</label>
                <select data-testid="new-payout-method" value={newSeller.payout.method}
                  onChange={(e) => setNewSeller({ ...newSeller, payout: { ...newSeller.payout, method: e.target.value } })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3">
                  <option value="paypal">PayPal</option>
                  <option value="remit">Remit / Mobile money</option>
                  <option value="bank">Bank transfer</option>
                </select>
              </div>
              {newSeller.payout.method === "paypal" && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">PayPal email</label>
                  <input data-testid="new-payout-paypal" type="email" value={newSeller.payout.paypal_email}
                    onChange={(e) => setNewSeller({ ...newSeller, payout: { ...newSeller.payout, paypal_email: e.target.value } })}
                    className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
              )}
              {newSeller.payout.method === "remit" && (
                <>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Phone (mobile money)</label>
                    <input data-testid="new-payout-phone" value={newSeller.payout.remit_phone}
                      onChange={(e) => setNewSeller({ ...newSeller, payout: { ...newSeller.payout, remit_phone: e.target.value } })}
                      className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Country</label>
                    <input data-testid="new-payout-country" value={newSeller.payout.remit_country}
                      onChange={(e) => setNewSeller({ ...newSeller, payout: { ...newSeller.payout, remit_country: e.target.value } })}
                      className="w-full mt-2 brut-border bg-white px-4 py-3" />
                  </div>
                </>
              )}
              {newSeller.payout.method === "bank" && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Bank details (free text)</label>
                  <textarea data-testid="new-payout-bank" rows={3} value={newSeller.payout.bank_details}
                    onChange={(e) => setNewSeller({ ...newSeller, payout: { ...newSeller.payout, bank_details: e.target.value } })}
                    className="w-full mt-2 brut-border bg-white px-4 py-3" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Product form (mirrors seller submit) */}
      <div className="pt-2 border-t-2 border-[#1F3D2A] space-y-4">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Product</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Title</label>
            <input data-testid="admin-prod-title" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>
          <div className="md:col-span-2">
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Description</label>
            <textarea data-testid="admin-prod-desc" required rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Price (USD)</label>
            <input data-testid="admin-prod-price" required type="number" step="0.01" min="0"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Type</label>
            <select data-testid="admin-prod-type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full mt-2 brut-border bg-white px-4 py-3">
              <option value="physical">Physical</option>
              <option value="digital">Digital</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Category / Section</label>
            <select data-testid="admin-prod-category" value={form.category}
              onChange={(e) => {
                const cat = e.target.value;
                setForm((f) => ({
                  ...f,
                  category: cat,
                  // streams are always digital — force the type so the physical shipping panel is hidden
                  type: cat === "stream" ? "digital" : f.type,
                }));
              }}
              className="w-full mt-2 brut-border bg-white px-4 py-3">
              <option value="book">Book</option>
              <option value="music">Music</option>
              <option value="art">Object / Art</option>
              <option value="apparel">Apparel</option>
              <option value="ticket">Ticket / Event</option>
              <option value="stream">Stream (Audiobook / Podcast / Video)</option>
              <option value="other">Other</option>
            </select>
          </div>

          {form.category === "stream" && (
            <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-4">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Stream details — buyer streams on Jubasmash, no downloads</div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Stream type *</label>
                <div className="mt-2 grid grid-cols-3 brut-border">
                  {[{k:"audiobook",n:"mp3 only"},{k:"podcast",n:"mp3 only"},{k:"video",n:"mp4 only"}].map((t, i) => (
                    <button key={t.k} type="button" data-testid={`admin-stream-type-${t.k}`}
                      onClick={() => setForm({ ...form, stream_type: t.k, chapters: [] })}
                      className={`py-3 mono uppercase text-xs tracking-[0.2em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${form.stream_type === t.k ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                      {t.k} <span className="opacity-60">· {t.n}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Chapters ({form.chapters.length}/20) · total {fmtDur(form.chapters.reduce((s,c)=>s+(c.duration_seconds||0),0))} / 2:00:00</label>
                  <label className="brut-border bg-[#1F3D2A] text-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#C77E3C] cursor-pointer">
                    + Add chapters
                    <input type="file" multiple className="hidden"
                      accept={form.stream_type === "video" ? "video/mp4,.mp4" : "audio/mpeg,audio/mp3,.mp3"}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []); e.target.value = "";
                        if (!files.length) return;
                        if (form.chapters.length + files.length > 20) { toast.error("Max 20 chapters"); return; }
                        for (const f of files) {
                          const badMime = form.stream_type === "video" ? !f.type.includes("mp4") : !f.type.includes("mpeg") && !f.type.includes("mp3");
                          if (badMime) { toast.error(`${f.name}: wrong format`); continue; }
                          if (f.size > 1024*1024*1024) { toast.error(`${f.name}: exceeds 1 GB`); continue; }
                          let duration; try { duration = await readDur(f); } catch { toast.error(`${f.name}: could not read duration`); continue; }
                          const stubIdx = form.chapters.length;
                          setForm((cur) => ({ ...cur, chapters: [...cur.chapters, { title: f.name.replace(/\.[^.]+$/, ""), duration_seconds: duration, size_bytes: f.size, mime: f.type, uploading: true }] }));
                          try {
                            const fd = new FormData();
                            fd.append("file", f);
                            fd.append("duration_seconds", String(duration));
                            fd.append("title", f.name.replace(/\.[^.]+$/, ""));
                            const { data } = await api.post("/streams/upload-chapter", fd, { headers: { "Content-Type": "multipart/form-data" } });
                            setForm((cur) => ({ ...cur, chapters: cur.chapters.map((c, i) => i === stubIdx ? { ...data, uploading: false } : c) }));
                            toast.success(`Uploaded: ${data.title}`);
                          } catch (err) {
                            toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
                            setForm((cur) => ({ ...cur, chapters: cur.chapters.filter((_, i) => i !== stubIdx) }));
                          }
                        }
                      }} />
                  </label>
                </div>
                <div className="mt-3 brut-border bg-white">
                  {form.chapters.length === 0 ? (
                    <div className="p-6 text-center mono text-[10px] uppercase tracking-[0.3em] text-[#555]">No chapters uploaded yet</div>
                  ) : form.chapters.map((c, i) => (
                    <div key={i} data-testid={`admin-stream-chapter-${i}`} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] w-8">{String(i+1).padStart(2,"0")}</div>
                      <input value={c.title} onChange={(e) => { const arr=[...form.chapters]; arr[i]={...arr[i], title: e.target.value}; setForm({...form, chapters: arr}); }}
                        className="flex-1 brut-border bg-[#FDF3E9] px-3 py-2 text-sm" />
                      <div className="mono text-[10px] tracking-[0.2em] w-16 text-right">{fmtDur(c.duration_seconds)}</div>
                      <div className="mono text-[9px] tracking-[0.2em] w-20 text-right text-[#555]">{(c.size_bytes/(1024*1024)).toFixed(1)} MB</div>
                      {c.uploading ? <span className="mono text-[9px] text-[#C77E3C] w-20 text-center">Uploading…</span> : (
                        <button type="button" onClick={() => setForm({ ...form, chapters: form.chapters.filter((_, j) => j !== i) })}
                          className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white w-20">Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">
                Recommended price $6 · No pornographic / excess violence content · Buyers get free first-3-minute preview
              </div>
            </div>
          )}

          {form.category === "ticket" && (
            <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-4">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Event details</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[0, 1, 2].map((idx) => (
                  <div key={idx}>
                    <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">
                      Date {idx + 1}{idx === 0 ? " *" : ""}
                    </label>
                    <input data-testid={`admin-event-date-${idx}`} type="date"
                      value={form.event_dates[idx] || ""}
                      onChange={(e) => { const arr = [...form.event_dates]; arr[idx] = e.target.value; setForm({ ...form, event_dates: arr }); }}
                      className="w-full mt-2 brut-border bg-white px-3 py-2 text-sm" />
                  </div>
                ))}
              </div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Location *</label>
                <input data-testid="admin-event-location" value={form.event_location}
                  onChange={(e) => setForm({ ...form, event_location: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3" />
              </div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Contact *</label>
                <input data-testid="admin-event-contact" value={form.event_contact}
                  onChange={(e) => setForm({ ...form, event_contact: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3" />
              </div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Tickets to sell *</label>
                <input data-testid="admin-event-capacity" type="number" min="1" value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="w-full mt-2 brut-border bg-white px-4 py-3" />
              </div>
            </div>
          )}

          {form.category === "apparel" && (
            <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-5">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Apparel details</div>
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Section *</label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 brut-border">
                  {["men", "women", "children", "accessories"].map((sec, i) => (
                    <button key={sec} type="button" data-testid={`admin-section-${sec}`}
                      onClick={() => setForm({ ...form, apparel_section: sec })}
                      className={`py-3 mono uppercase text-xs tracking-[0.2em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${form.apparel_section === sec ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                      {sec}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                <input data-testid="admin-one-size" type="checkbox" checked={form.one_size}
                  onChange={(e) => setForm({ ...form, one_size: e.target.checked })} />
                <span className="mono text-xs uppercase tracking-[0.2em]">One size</span>
              </label>
              {!form.one_size && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Sizes</label>
                  <div className="mt-2 flex flex-wrap gap-2 mb-2">
                    {["XS", "S", "M", "L", "XL", "XXL", "3XL"].map((s) => (
                      <button key={s} type="button"
                        onClick={() => {
                          if ((form.sizes || []).includes(s)) return;
                          if ((form.sizes || []).length >= 20) return;
                          setForm({ ...form, sizes: [...(form.sizes || []), s] });
                        }}
                        className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">+ {s}</button>
                    ))}
                  </div>
                  {(form.sizes || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.sizes.map((s, i) => (
                        <span key={i} className="brut-border bg-[#1F3D2A] text-white pl-3 pr-1 py-1 mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                          {s}
                          <button type="button" onClick={() => setForm({ ...form, sizes: form.sizes.filter((_, j) => j !== i) })} className="hover:text-[#C77E3C] px-2">✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Colours (up to 10)</label>
                <div className="mt-3 space-y-2">
                  {(form.colors || []).map((c, idx) => (
                    <div key={idx} className="flex items-center gap-3 brut-border bg-white p-2">
                      <div className="w-14 h-14 brut-border bg-[#EAEAEA] overflow-hidden flex-shrink-0">
                        {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                      </div>
                      <input placeholder="Colour name" value={c.name} onChange={(e) => {
                        const arr = [...form.colors]; arr[idx] = { ...arr[idx], name: e.target.value }; setForm({ ...form, colors: arr });
                      }} className="flex-1 brut-border bg-white px-3 py-2 text-sm" />
                      <label className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] cursor-pointer hover:bg-[#1F3D2A] hover:text-white">
                        {c.image_url ? "Change" : "Upload"}
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const fd = new FormData(); fd.append("file", f);
                          const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          const arr = [...form.colors]; arr[idx] = { ...arr[idx], image_url: `${BACKEND_URL}${data.url}` }; setForm({ ...form, colors: arr });
                        }} />
                      </label>
                      <button type="button" onClick={() => setForm({ ...form, colors: form.colors.filter((_, j) => j !== idx) })} className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C77E3C] hover:text-white">✕</button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => {
                  if ((form.colors || []).length >= 10) { toast.error("Max 10 colours"); return; }
                  setForm({ ...form, colors: [...(form.colors || []), { name: "", image_url: "" }] });
                }} className="mt-3 brut-border bg-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-[#1F3D2A] hover:text-white">+ Add colour</button>
              </div>
              {((form.one_size ? ["ONE SIZE"] : (form.sizes || [])).length > 0) && (
                <div>
                  <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Stock per variant</label>
                  <div className="overflow-x-auto brut-border bg-white mt-2">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1F3D2A] text-white">
                        <tr className="mono text-[10px] uppercase tracking-[0.2em]">
                          <th className="p-2 text-left">Size ↓ / Colour →</th>
                          {((form.colors || []).filter((c) => c.name).length ? form.colors.filter((c) => c.name).map((c) => c.name) : ["—"]).map((cn) => (
                            <th key={cn} className="p-2 text-center">{cn}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(form.one_size ? ["ONE SIZE"] : form.sizes).map((s) => {
                          const cols = (form.colors || []).filter((c) => c.name).length ? form.colors.filter((c) => c.name).map((c) => c.name) : [""];
                          return (
                            <tr key={s} className="border-t-2 border-[#1F3D2A]">
                              <td className="p-2 mono uppercase text-xs font-bold">{s}</td>
                              {cols.map((ck) => {
                                const key = `${s}|${ck}`;
                                return (
                                  <td key={key} className="p-2">
                                    <input type="number" min="0" value={form.variant_stock?.[key] ?? 0}
                                      onChange={(e) => setForm({ ...form, variant_stock: { ...(form.variant_stock || {}), [key]: parseInt(e.target.value) || 0 } })}
                                      className="w-20 brut-border bg-white px-2 py-1 text-center mono" />
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {form.type === "physical" && (
            <div className="md:col-span-2 brut-border bg-[#FDF3E9] p-5 space-y-4">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Shipping</div>
              <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                <input data-testid="admin-free-shipping" type="checkbox" checked={form.free_shipping}
                  onChange={(e) => setForm({ ...form, free_shipping: e.target.checked, allow_pickup: false })} />
                <span className="mono text-xs uppercase tracking-[0.2em]">Free shipping (seller absorbs cost)</span>
              </label>
              {!form.free_shipping && (
                <>
                  <div>
                    <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Shipping price (USD)</label>
                    <input data-testid="admin-shipping-price" type="number" step="0.01" min="0" value={form.shipping_price}
                      onChange={(e) => setForm({ ...form, shipping_price: e.target.value })}
                      className="w-full mt-2 brut-border bg-white px-4 py-3" placeholder="e.g. 5.99" />
                  </div>
                  <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                    <input data-testid="admin-allow-pickup" type="checkbox" checked={form.allow_pickup}
                      onChange={(e) => setForm({ ...form, allow_pickup: e.target.checked })} />
                    <span className="mono text-xs uppercase tracking-[0.2em]">Also offer local pickup (uses artisan's registered city+country)</span>
                  </label>
                  <label className="flex items-center gap-2 brut-border bg-white px-4 py-3 cursor-pointer w-fit">
                    <input data-testid="admin-vat-included" type="checkbox" checked={form.shipping_vat_included}
                      onChange={(e) => setForm({ ...form, shipping_vat_included: e.target.checked })} />
                    <span className="mono text-xs uppercase tracking-[0.2em]">Shipping price already includes VAT / tax</span>
                  </label>
                </>
              )}
            </div>
          )}

          {form.type === "physical" && form.category !== "ticket" && form.category !== "apparel" && (
            <div>
              <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Stock</label>
              <input data-testid="admin-prod-stock" type="number" min="0" value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full mt-2 brut-border bg-white px-4 py-3" />
            </div>
          )}

          {form.type === "physical" && (
            <div>
              <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Delivery window</label>
              <div className="mt-2 grid grid-cols-2 brut-border">
                {[15, 30].map((d, i) => (
                  <button key={d} type="button" data-testid={`admin-delivery-${d}`}
                    onClick={() => setForm({ ...form, delivery_days: d })}
                    className={`py-3 mono uppercase text-xs tracking-[0.25em] ${i > 0 ? "border-l-2 border-[#1F3D2A]" : ""} ${form.delivery_days === d ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                    {d} days
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Cover image(s)</label>
            <input data-testid="admin-prod-images" type="file" accept="image/*" multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const f of files) await uploadImage(f);
                e.target.value = "";
              }}
              className="w-full mt-2 brut-border bg-white px-4 py-3" />
            {form.images && form.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {form.images.map((url, idx) => (
                  <div key={idx} className="relative w-24 h-24 brut-border overflow-hidden group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {idx === 0 && <span className="absolute top-0 left-0 bg-[#C77E3C] text-white mono text-[8px] px-1 uppercase">Main</span>}
                    <button type="button" onClick={() => removeImage(url)}
                      className="absolute bottom-0 right-0 bg-[#1F3D2A] text-white mono text-[10px] px-1 opacity-0 group-hover:opacity-100">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {form.type === "digital" && (
            <div className="md:col-span-2">
              <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Digital file (PDF, MP3, ZIP)</label>
              <input data-testid="admin-prod-digital" type="file"
                onChange={(e) => e.target.files?.[0] && uploadDigital(e.target.files[0])}
                className="w-full mt-2 brut-border bg-white px-4 py-3" />
              {form.digital_file_path && <div className="mt-2 mono text-xs text-[#555]">Uploaded: {form.digital_file_path}</div>}
            </div>
          )}

          {(form.category === "music" || form.category === "book") && (
            <div className="md:col-span-2">
              <label className="mono text-xs uppercase tracking-[0.2em] font-bold">
                {form.category === "music" ? "Audio preview (MP3 sample)" : "Sample PDF (first few pages)"}
              </label>
              <input data-testid="admin-prod-preview" type="file"
                accept={form.category === "music" ? "audio/*" : "application/pdf"}
                onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])}
                className="w-full mt-2 brut-border bg-white px-4 py-3" />
              {form.preview_url && <div className="mt-2 mono text-xs text-[#555]">Preview uploaded ✓</div>}
            </div>
          )}
        </div>
      </div>

      <button data-testid="admin-submit-product" disabled={submitting} className="btn-primary">
        {submitting ? "Adding…" : "Add product (auto-approved)"}
      </button>
    </form>
  );
}
